// Voice notification utility
// This utility handles playing voice notifications for order events

let audioInstance = null;
let isAudioReady = false;
let audioLoadAttempted = false;

// Preload audio to avoid browser autoplay restrictions
const preloadAudio = () => {
  if (audioLoadAttempted) {
    return; // Already attempted to load
  }
  
  if (!audioInstance) {
    try {
      audioLoadAttempted = true;
      audioInstance = new Audio("/order-placed-voice.mp3");
      audioInstance.volume = 0.7; // Set volume to 70%
      audioInstance.preload = "auto";
      
      // Mark as ready when loaded
      audioInstance.addEventListener("canplaythrough", () => {
        isAudioReady = true;
        console.log("🔊 Voice notification audio ready");
      });
      
      // Also mark as ready when canplay fires (less strict than canplaythrough)
      audioInstance.addEventListener("canplay", () => {
        if (!isAudioReady) {
          isAudioReady = true;
          console.log("🔊 Voice notification audio can play");
        }
      });
      
      // Handle errors
      audioInstance.addEventListener("error", (e) => {
        console.error("❌ Audio loading error:", e);
        console.error("❌ Audio error details:", {
          code: audioInstance.error?.code,
          message: audioInstance.error?.message,
        });
        isAudioReady = false;
      });
      
      // Load the audio
      audioInstance.load().catch((error) => {
        console.error("❌ Error loading audio:", error);
      });
    } catch (error) {
      console.error("❌ Error creating audio instance:", error);
      isAudioReady = false;
    }
  }
};

// Initialize audio on module load
if (typeof window !== "undefined") {
  // Preload audio immediately when module loads (for better compatibility)
  if (document.readyState === "complete" || document.readyState === "interactive") {
    // DOM is already ready, try to preload immediately
    preloadAudio();
  } else {
    document.addEventListener("DOMContentLoaded", () => {
      preloadAudio();
    });
  }
  
  // Also preload on any user interaction (to satisfy autoplay policies)
  const initAudioOnInteraction = () => {
    if (!audioInstance) {
      preloadAudio();
    }
  };
  
  // Try to preload on any user interaction
  document.addEventListener("click", initAudioOnInteraction, { once: true });
  document.addEventListener("touchstart", initAudioOnInteraction, { once: true });
  document.addEventListener("keydown", initAudioOnInteraction, { once: true });
  document.addEventListener("mousedown", initAudioOnInteraction, { once: true });
}

/**
 * Play order placed voice notification
 * This function handles browser autoplay restrictions by:
 * 1. Using a preloaded audio instance
 * 2. Handling user interaction requirements
 * 3. Providing fallback error handling
 */
export const playOrderPlacedVoice = () => {
  try {
    console.log("🔊 Attempting to play order placed voice notification");
    
    // If audio is not initialized, create it now
    if (!audioInstance && !audioLoadAttempted) {
      preloadAudio();
      // Wait a bit for audio to load
      setTimeout(() => {
        playOrderPlacedVoice();
      }, 100);
      return;
    }
    
    // If audio instance exists, try to play it
    if (audioInstance) {
      // Reset to beginning if already played
      if (audioInstance.currentTime > 0) {
        audioInstance.currentTime = 0;
      }
      
      // Ensure audio is loaded
      if (audioInstance.readyState >= 2) { // HAVE_CURRENT_DATA or higher
        // Play the audio
        const playPromise = audioInstance.play();
        
        if (playPromise !== undefined) {
          playPromise
            .then(() => {
              console.log("✅ Voice notification played successfully");
            })
            .catch((error) => {
              // Autoplay was prevented - try fallback
              console.warn("⚠️ Autoplay prevented:", error.name, error.message);
              // Try fallback immediately
              tryFallbackAudio();
            });
        } else {
          console.warn("⚠️ Audio play() returned undefined, trying fallback");
          tryFallbackAudio();
        }
      } else {
        // Audio not ready yet, wait and try again
        console.log("⚠️ Audio not ready yet, waiting...");
        audioInstance.addEventListener("canplay", () => {
          const playPromise = audioInstance.play();
          if (playPromise !== undefined) {
            playPromise.catch((error) => {
              console.warn("⚠️ Audio play failed after ready:", error);
              tryFallbackAudio();
            });
          }
        }, { once: true });
      }
    } else {
      // Fallback: create a new audio instance and try to play
      tryFallbackAudio();
    }
  } catch (error) {
    console.error("❌ Error playing voice notification:", error);
    tryFallbackAudio();
  }
};

// Helper function to try fallback audio
const tryFallbackAudio = () => {
  console.log("⚠️ Trying fallback audio instance");
  try {
    const fallbackAudio = new Audio("/order-placed-voice.mp3");
    fallbackAudio.volume = 0.7;
    // Try to play immediately
    const playPromise = fallbackAudio.play();
    if (playPromise !== undefined) {
      playPromise
        .then(() => {
          console.log("✅ Fallback voice notification played successfully");
        })
        .catch((error) => {
          console.warn("⚠️ Fallback audio play failed:", error);
          // Last resort: try to play on next user interaction
          const playOnInteraction = () => {
            fallbackAudio.play()
              .then(() => {
                console.log("✅ Fallback voice notification played after user interaction");
              })
              .catch((err) => {
                console.warn("❌ Could not play voice notification after interaction:", err);
              });
            document.removeEventListener("click", playOnInteraction);
            document.removeEventListener("touchstart", playOnInteraction);
          };
          document.addEventListener("click", playOnInteraction, { once: true });
          document.addEventListener("touchstart", playOnInteraction, { once: true });
        });
    }
  } catch (error) {
    console.error("❌ Error creating fallback audio:", error);
  }
};

// Export function to manually initialize audio (useful for testing)
export const initializeVoiceNotification = () => {
  preloadAudio();
};

